<script>
    import { onMount } from "svelte";
    import {
        getSymbolPrice,
        calculatePetroleumPrice,
        formatNumber,
        trackImpression,
        trackEngagement,
    } from "./utils";
    import Slider from "./components/Slider.svelte";
    import LcdNumber from "./components/LcdNumber.svelte";
    import Method from "./components/Method.svelte";
    import LitersSelector from "./components/LitersSelector.svelte";

    let mainEl;

    let observer;

    function attachOnImpressionListener(el) {
        if (el && !observer) {
            observer = new IntersectionObserver((entries) => {
                entries.forEach(({ isIntersecting }) => {
                    if (isIntersecting) {
                        trackImpression();

                        observer.unobserve(el);
                    }
                });
            });

            observer.observe(el);
        }
    }

    const OIL_MIN = 15;
    const OIL_MAX = 150;

    const USDNOK_MIN = 7.5;
    const USDNOK_MAX = 12;

    let markers = [
        { name: "3. jan", color: "var(--color-gray)", oil: 78.96, usdNok: 8.87 },
        { name: "22. feb", color: "var(--color-dark-gray)", oil: 96.64, usdNok: 8.9 },
        {
            name: "8. mars",
            subtitle: "(Høyeste siden 22.feb)",
            color: "var(--color-interaction-2)",
            oil: 129.2,
            usdNok: 8.99,
        },
    ];

    let oilPrice;
    let usdNok;
    let liters = 1;
    let gasolinePrice;
    let hasInteracted = false;

    let currentOilPrice, currentUsdNok, currentGasolinePrice;

    function updatePrices(oil, usd) {
        oilPrice = oil;
        usdNok = usd;
    }

    onMount(async () => {
        updateGasolinePrice(oilPrice, usdNok);

        hasInteracted = false;

        [currentOilPrice, currentUsdNok] = await Promise.all([
            getSymbolPrice("C%3APBROUSDBR%5CSP.IDCENE"),
            getSymbolPrice("X%3ASUSDNOK.IDCFX"),
        ]);
        currentGasolinePrice = calculatePetroleumPrice(
            currentOilPrice,
            "GASOLINE",
            currentUsdNok
        );

        markers = [
            ...markers,
            {
                name: "I dag",
                color: "var(--color-breaking)",
                oil: currentOilPrice,
                usdNok: currentUsdNok,
            },
        ];

        updatePrices(currentOilPrice, currentUsdNok);
    });

    function updateGasolinePrice(oilPrice, usdNok) {
        gasolinePrice = calculatePetroleumPrice(oilPrice, "GASOLINE", usdNok);

        if (currentGasolinePrice && gasolinePrice !== currentGasolinePrice) {
            hasInteracted = true;
        }
    }

    let showMethod = false;

    function toggleMethod() {
        showMethod = !showMethod;
    }

    function onMarkerClick(name, markerOilPrice, markerUsdNok) {
        trackEngagement("date-marker", name);

        updatePrices(markerOilPrice, markerUsdNok);
    }

    $: attachOnImpressionListener(mainEl);
    $: updateGasolinePrice(oilPrice, usdNok);
</script>

<style>
    :root {
        --color-dark: #1D1D1D;
        --color-light: #FFFBF8;
        --color-gray: #777777;
        --color-light-gray: #9A9A9A;
        --color-dark-gray: #444444;
        --color-breaking: #e70349;
        --color-interaction: #4127C8;
        --color-interaction-2: #7a71fe;

        --margin: 12px;
        --margin-large: 18px;
        --margin-xlarge: 24px;
        --margin-small: 8px;
    }

    main {
        font-family: "E24 Text", sans-serif;
        -webkit-text-size-adjust: 100%;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        line-height: 1.3;
        text-align: left;
        padding: var(--margin);
        margin: 0 auto;
        background: var(--color-dark);
        border-radius: 16px;
        border: 1px solid var(--color-light);
        color: var(--color-light);
    }

    main * {
        user-select: none;
    }

    .oil-price-inline,
    .currency-inline {
        display: inline-block;
        font-feature-settings: "tnum";
        font-variant-numeric: tabular-nums;
        text-align: center;
    }

    .oil-price-inline {
        width: 40px;
    }

    .currency-inline {
        width: 50px;
    }

    .title {
        font-family: "E24 Display", sans-serif;
        font-size: 44px;
        font-weight: 700;
        margin-top: 0;
        margin-bottom: var(--margin-small);
    }

    .description {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        min-height: 36px;
        font-size: 14px;
        text-align: left;
    }

    .box {
        background-color: var(--color-light);
        border-radius: 16px;
        margin: var(--margin-small) 0;
        padding: var(--margin) var(--margin) var(--margin-small);
        color: var(--color-dark);
        position: relative;
    }

    .slider-card {
        margin: 0;
        padding: 0;
    }

    .box.show-method .slider-card {
        display: none;
    }

    .disclaimer {
        font-size: 12px;
        margin: var(--margin) 0;
    }

    .disclaimer.bottom-disclaimer {
        margin-bottom: 0;
    }

    .read-method {
        color: var(--color-interaction-2);
        text-decoration: underline;
        cursor: pointer;
    }

    .legend {
        display: flex;
        justify-content: center;
        font-size: 12px;
        margin-top: var(--margin-xlarge);
    }

    .legend .item {
        cursor: pointer;
        margin-right: 18px;
    }

    .marker-subtitle {
        width: 100%;
        display: block;
        font-size: 10px;
        color: var(--color-dark-gray);
        max-width: 66px;
    }

    .legend .item:last-child {
        margin-right: 0;
    }

    .legend div.item:before {
        content: "";
        width: 10px;
        height: 10px;
        display: inline-block;
        border-radius: 16px;
        margin-right: 6px;
        position: relative;
        top: 0px;
        background-color: var(--marker-color);
    }

    @media (min-width: 600px) {
        .title {
            font-size: 50px;
        }

        .description {
            min-height: 47px;
            font-size: 18px;
        }

        .disclaimer {
            margin: var(--margin-large) 0;
            font-size: 14px;
        }

        main,
        .box {
            padding: var(--margin-large) var(--margin-large)
                var(--margin-small);
        }

        .oil-price-inline {
            width: 52px;
        }

        .currency-inline {
            width: 64px;
        }
    }

    @media (max-width: 550px) {
        .slider-card .description {
            display: none;
        }
    }

    @media (max-width: 375px) {
        .title {
            font-size: 40px;
        }
    }

    @media (max-width: 345px) {
        .title {
            font-size: 34px;
        }
        .marker-subtitle {
            font-size: 8px;
        }
    }

    @media (max-width: 350px) {
        .legend {
            margin-top: 20px;
            justify-content: space-around;
        }

        .legend .item {
            text-align: center;
        }

        .legend div.item:before {
            display: block;
            margin: 0 auto 8px;
        }
    }

    @media (max-width: 320px) {
        .title {
            font-size: 32px;
        }
    }
</style>

<main bind:this={mainEl}>
    <div>
        <h2 class="title">Pumpesjokket</h2>
        <div class="description">
            {#if hasInteracted}
                <span>Med oljepris på
                    <span class="oil-price-inline">$
                        {formatNumber(oilPrice, 0)}</span>
                    og dollarpris på
                    <span class="currency-inline">{formatNumber(usdNok, 1)}
                        kr</span>
                    kan vi beregne* bensinprisen til ca:</span>
            {:else}
                Med dagens oljepris og dollarkurs kan vi beregne* bensinprisen
                til ca:
            {/if}
        </div>
        <LcdNumber number={gasolinePrice} liters={liters} />
        <LitersSelector onUpdate={(newValue) => liters = newValue} liters={liters} />
        <div class="top-disclaimer disclaimer">
            Faktiske priser varierer basert på stasjon og tid.
        </div>
        <div class={`box ${showMethod ? 'show-method' : ''}`}>
            <div class="slider-card">
                <div class="description">
                    Prøv ut forskjellige markedspriser og se hvordan de henger
                    sammen med pumpeprisen.
                </div>
                <Slider
                    label="Oljeprisen"
                    currentValue={currentOilPrice}
                    value={oilPrice}
                    {markers}
                    markerValueKey="oil"
                    min={OIL_MIN}
                    max={OIL_MAX}
                    decimals={0}
                    formatValue={(value) => `$ ${value}`}
                    onUpdate={(value) => (oilPrice = value)} />
                <Slider
                    label="Dollarkursen"
                    currentValue={currentUsdNok}
                    value={usdNok}
                    {markers}
                    markerValueKey="usdNok"
                    min={USDNOK_MIN}
                    max={USDNOK_MAX}
                    decimals={1}
                    formatValue={(value) => `${value} kr`}
                    onUpdate={(value) => (usdNok = value)} />
                <div class="legend">
                    {#each markers as { name, color, oil, usdNok, subtitle }}
                        <div
                            class="item"
                            style={`--marker-color: ${color}`}
                            on:click={() => onMarkerClick(name, oil, usdNok)}>
                            {name}
                            {#if subtitle}
                                <span class="marker-subtitle">{subtitle}</span>
                            {/if}
                        </div>
                    {/each}
                </div>
            </div>
            <Method active={showMethod} {toggleMethod} />
        </div>
        <div class="bottom-disclaimer disclaimer">
            * Beregning basert forhold mellom oljeprisen og bensinprise mellom
            fra 2018 til 2021 og er derfor ikke egnet til å se bakover.
            <span on:click={toggleMethod} class="read-method">Les mer</span>
        </div>
    </div>
</main>
